import * as yup from 'yup'
import * as _ from './message'
import { getRestrictedCountryOfResidenceValidationError } from './restricted-countries'

const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/

const signInSchema = yup.object().shape({
  email: yup.string().required(_.EMAIL_REQUIRED_ERROR).email(_.EMAIL_ERROR),
  password: yup
    .string()
    .required(_.PASSWORD_REQUIRED_ERROR)
    .matches(passwordRegex, _.PASSWORD_ERROR),
})

const adminSignInSchema = yup.object().shape({
  adminEmail: yup
    .string()
    .required(`Admin ${_.EMAIL_REQUIRED_ERROR.toLowerCase()}`)
    .email(_.EMAIL_ERROR),
  investorEmail: yup
    .string()
    .required(`Client ${_.EMAIL_REQUIRED_ERROR.toLowerCase()}`)
    .email(_.EMAIL_ERROR),
  password: yup
    .string()
    .required(_.PASSWORD_REQUIRED_ERROR)
    .matches(passwordRegex, _.PASSWORD_ERROR),
})

const signUpSchema = yup.object().shape({
  name: yup.string().required(_.FULL_NAME_REQUIRED_ERROR),
  email: yup.string().required(_.EMAIL_REQUIRED_ERROR).email(_.EMAIL_ERROR),
  password: yup
    .string()
    .required(_.PASSWORD_REQUIRED_ERROR)
    .matches(passwordRegex, _.PASSWORD_ERROR),
  passwordConfirmation: yup
    .string()
    .required(_.CONFIRM_PASSWORD_REQUIRED_ERROR)
    .oneOf([yup.ref('password'), null], _.CONFIRM_PASSWORD_ERROR),
  referralCode: yup.string().optional(),
})

const signUpV2Schema = signUpSchema.shape({
  termsAccepted: yup
    .boolean()
    .oneOf([true], 'You must accept the Terms of Service and Privacy Policy'),
})

const forgotPasswordSchema = yup.object({
  password: yup
    .string()
    .required(_.PASSWORD_REQUIRED_ERROR)
    .matches(passwordRegex, _.PASSWORD_ERROR),
  passwordConfirmation: yup
    .string()
    .required(_.CONFIRM_PASSWORD_REQUIRED_ERROR)
    .oneOf([yup.ref('password'), null], _.CONFIRM_PASSWORD_ERROR),
})

const emailInputSchema = yup.object({
  email: yup.string().required(_.EMAIL_REQUIRED_ERROR).email(_.EMAIL_ERROR),
})

const kycAll = yup.object({
  curriculamVitae: yup.mixed().nullable().required(),
  documentEvidencingSourceOfFundWealth: yup.mixed().nullable().required(),
})
const kycIndividualUploads = kycAll.shape({
  personalIdentityCertificate: yup.mixed().nullable().required(),
  addressVerification: yup.mixed().nullable().required(),
})
const kycLLP = kycAll.shape({
  profileOfTheCorporation: yup.mixed().nullable().required(),
  partnershipAgreementOrLlcOperatingAgreement: yup.mixed().nullable().required(),
  lpOrLlcMandateAuthorizingTheEstablishmentOfTheRelationship: yup.mixed().nullable().required(),
  conferringAuthorityOnThoseWhoIssueInstructions: yup.mixed().nullable().required(),
  registeredAddressVerificationOfCorporation: yup.mixed().nullable().required(),
})
const kycCorporateEntity = kycAll.shape({
  profileOfTheCorporation: yup.mixed().nullable().required(),
  authorizedSignatoryList: yup.mixed().nullable().required(),
  listOfDirectorsNames: yup.mixed().nullable().required(),
  certificateOfIncorporationOrSimilarDocument: yup.mixed().nullable().required(),
  personalIdentityAndAddressVerificationOfAllDirectors: yup.mixed().nullable().required(),
  listOfNamesAndAddressesOfShareholdersHolding10OrMore: yup.mixed().nullable().required(),
})
const kycInvestmentFund = kycAll.shape({
  profileOfTheCorporation: yup.mixed().nullable().required(),
  authorizedSignatoryList: yup.mixed().nullable().required(),
  listOfDirectorsNames: yup.mixed().nullable().required(),
  certificateOfIncorporationOrSimilarDocument: yup.mixed().nullable().required(),
  personalIdentityAndAddressVerificationOfAllDirectors: yup.mixed().nullable().required(),
  writtenConfirmationByTheAdministratorOfTheFund: yup.mixed().nullable().required(),
})

const kycPrivateFoundations = kycAll.shape({
  profileOfTheCorporation: yup.mixed().nullable().required(),
  authorizedSignatoryList: yup.mixed().nullable().required(),
  listOfDirectorsNames: yup.mixed().nullable().required(),
  personalIdentityAndAddressVerificationOfAllDirectors: yup.mixed().nullable().required(),
  personalIdentityAndAddressVerificationOfTheFounder: yup.mixed().nullable().required(),
  certificateOfIncorporationOrSimilarDocument: yup.mixed().nullable().required(),
})

const contactUsDetailsSchema = yup.object().shape({
  firstName: yup.string().required(_.FIRST_NAME_REQUIRED_ERROR),
  lastName: yup.string().required(_.LAST_NAME_REQUIRED_ERROR),
  email: yup.string().required(_.EMAIL_REQUIRED_ERROR).email(_.EMAIL_ERROR),
  subject: yup.string().required(),
  message: yup.string().max(500).required(_.MESSAGE_REQUIRED_ERROR),
})

const businessContactSchema = yup.object().shape({
  firstName: yup.string().required(_.FIRST_NAME_REQUIRED_ERROR),
  lastName: yup.string().required(_.LAST_NAME_REQUIRED_ERROR),
  company: yup.string().required('Company name is required'),
  email: yup.string().required(_.EMAIL_REQUIRED_ERROR).email(_.EMAIL_ERROR),
  message: yup.string().max(500).required(_.MESSAGE_REQUIRED_ERROR),
})
const basicDetailsSchema = yup.object({
  firstName: yup.string().required(_.FIRST_NAME_REQUIRED_ERROR),
  lastName: yup.string().required(_.LAST_NAME_REQUIRED_ERROR),
  dateOfBirth: yup
    .date()
    .nullable()
    .required(_.isRequiredMessage('Date of birth'))
    .max(new Date(), 'Date of birth cannot be in the future')
    .typeError(_.isRequiredMessage('Date of birth')),
  countryOfResidence: yup
    .string()
    .required(_.isRequiredMessage('Country of residence'))
    .test('restricted-country', function (value) {
      const error = getRestrictedCountryOfResidenceValidationError(value ?? '')
      if (error) {
        return this.createError({ message: error })
      }
      return true
    }),
  taxResidency: yup.string().required(_.isRequiredMessage('Tax residency')),
  citizenship: yup.string().required(_.isRequiredMessage('Citizenship')),
  callingPhone: yup.string().required(_.isRequiredMessage('Phone number')),
  clientCv: yup
    .string()
    .trim()
    .required(_.isRequiredMessage('Tell us more about yourself'))
    .max(500),
})

const investorGeneralSchema = yup.object({
  name: yup.string().required(_.isRequiredMessage('Name')),
  countryOfResidence: yup.string().required(_.isRequiredMessage('Country')),
  citizenship: yup.string().required(_.isRequiredMessage('Citizenship')),
  email: yup.string(),
  phoneNumber: yup.string(),
  whatsappConsent: yup.boolean(),
})

const investorDetailSchema = yup.object({
  passportNumber: yup.string(),
  citizenship: yup.string(),
})

const investmentDetails = yup.object({
  name: yup.string().required(_.isRequiredMessage('Full Name')),
  dob: yup.date().required(_.isRequiredMessage('Date of birth')),
  countryOfResidence: yup.string().required(_.isRequiredMessage('Country')),
  city: yup.string().required(_.isRequiredMessage('City')),
  zip: yup.string().min(5).max(6).required(_.isRequiredMessage('ZIP Code')),
  addressLine1: yup.string().required(_.isRequiredMessage('Address Line 1')),
  addressLine2: yup.string().required(_.isRequiredMessage('Address Line 2')),
  phoneNumber: yup.string().min(10).required(_.isRequiredMessage('Phone number')),
  email: yup.string().required(_.isRequiredMessage('Email')),
})

const contactUsSchema = yup.object({
  name: yup.string().required(_.FULL_NAME_REQUIRED_ERROR),
  email: yup.string().required(_.EMAIL_REQUIRED_ERROR),
  phone: yup.string().min(10).required('Phone number cannot be blank'),
  assistance_category: yup.string().required('Assistance category cannot be blank'),
  subject: yup.string().required('Subject cannot be blank'),
  message: yup.string().required('Message cannot be blank'),
  attachments: yup.array().optional(),
})

export const bankDetailsSchema = yup.object().shape({
  beneficiaryName: yup
    .string()
    .required('Beneficiary name is required')
    .min(2, 'Beneficiary name must be at least 2 characters')
    .max(100, 'Beneficiary name must not exceed 100 characters'),
  accountNumber: yup
    .string()
    .required('Account number is required')
    .matches(/^[0-9]+$/, 'Account number must contain only numbers')
    .min(8, 'Account number must be at least 8 digits')
    .max(20, 'Account number must not exceed 20 digits'),
  beneficiaryBank: yup
    .string()
    .required('Bank name is required')
    .min(2, 'Bank name must be at least 2 characters')
    .max(100, 'Bank name must not exceed 100 characters'),
  bankAddress: yup
    .string()
    .required('Bank address is required')
    .min(5, 'Bank address must be at least 5 characters')
    .max(200, 'Bank address must not exceed 200 characters'),
  cif: yup
    .string()
    .notRequired()
    .test(
      'cif-length',
      'CIF must be between 5 and 20 characters',
      (value) => !value || (value.length >= 5 && value.length <= 20),
    )
    .test(
      'cif-format',
      'CIF must contain only letters and numbers',
      (value) => !value || /^[A-Za-z0-9]+$/.test(value),
    ),
  swiftCode: yup
    .string()
    .required('SWIFT code is required')
    .matches(/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/, 'Invalid SWIFT code format')
    .min(8, 'SWIFT code must be at least 8 characters')
    .max(11, 'SWIFT code must not exceed 11 characters'),
})

const feedbackSchema = yup.object().shape({
  description: yup
    .string()
    .required('Description is required')
    .min(10, 'Description must be at least 10 characters')
    .max(500, 'Description must not exceed 500 characters'),
})

export {
  signInSchema,
  signUpSchema,
  signUpV2Schema,
  forgotPasswordSchema,
  emailInputSchema,
  basicDetailsSchema,
  investorGeneralSchema,
  investorDetailSchema,
  contactUsDetailsSchema,
  businessContactSchema,
  kycPrivateFoundations,
  kycInvestmentFund,
  kycLLP,
  kycIndividualUploads,
  kycCorporateEntity,
  investmentDetails,
  contactUsSchema,
  feedbackSchema,
  adminSignInSchema,
}
